import { type FC, Fragment, useRef, useState, type MouseEvent } from 'react'
import { Collapse, List, ListItem, ListItemIcon, ListItemText, Typography } from '@mui/material'
// eslint-disable-next-line no-restricted-imports -- Use sx props or styled components to create CSS
import { ExpandLess, ExpandMore } from '@dhl/icons'

import { SideMenuListItem, styles } from './SideMenuListItem.js'
import { type MenuItemWithSubItems } from './types.js'
import { SideMenuInline } from './SideMenuInline.js'

type Props = {
  disableTab: boolean
  isExpanded: boolean
  toggleExpanded?: () => void
  menuItems: MenuItemWithSubItems[]
  navigationPath?: string
}

const isActive = (navigationPath: string | undefined, link: string | undefined): boolean => {
  if (!navigationPath || !link) {
    return false
  }
  return navigationPath.startsWith(link)
}

export const SideMenuWithCollapsedSubmenu: FC<Props> = ({
  disableTab,
  isExpanded,
  menuItems,
  navigationPath,
  toggleExpanded,
}) => {
  const [openSub, setOpenSub] = useState(false)
  const [manualClose, setManualClose] = useState(false)
  const isActiveSub = useRef(false)

  const handleToggle = () => {
    if (toggleExpanded) {
      toggleExpanded()
    }
    setOpenSub(false)
    setManualClose(false)
  }

  const handleSubClick = () => {
    if (isActiveSub.current && !manualClose) {
      setManualClose(true)
      setOpenSub(false)
    } else if (isActiveSub.current && manualClose) {
      setManualClose(false)
    } else {
      setOpenSub(!openSub)
    }
  }

  const handleMenuWithSubClick = (event: MouseEvent<SVGSVGElement> | undefined) => {
    event?.stopPropagation()
    event?.preventDefault()
    handleSubClick()
  }

  return (
    <>
      {menuItems.map((item) => {
        if (item.subMenuItems) {
          isActiveSub.current = item.subMenuItems.some((subItem) => isActive(navigationPath, subItem.link))

          return (
            <Fragment key={item.key}>
              {isExpanded && (
                <ListItem id={item.id} onClick={handleSubClick} sx={{ cursor: 'pointer' }}>
                  <ListItemIcon sx={{ pl: '4px', my: '8px' }}>{item.icon}</ListItemIcon>
                  <ListItemText primary={<Typography variant="body2">{item.label}</Typography>} disableTypography />

                  {openSub || (isActiveSub.current && !manualClose) ? (
                    <ExpandLess onClick={handleMenuWithSubClick} />
                  ) : (
                    <ExpandMore onClick={handleMenuWithSubClick} />
                  )}
                </ListItem>
              )}
              {!isExpanded && (
                <ListItemIcon
                  sx={
                    isActiveSub.current
                      ? { px: '18px', my: '18px', ...styles.activeMenuItem }
                      : { px: '18px', my: '18px', ...styles.menuItem }
                  }
                  onClick={() => handleToggle()}
                >
                  {item.icon}
                </ListItemIcon>
              )}

              <Collapse in={(openSub || (isActiveSub.current && !manualClose)) && isExpanded} timeout="auto">
                <List
                  component="div"
                  disablePadding
                  sx={{
                    pb: 2,
                  }}
                >
                  <SideMenuInline
                    disableTab={disableTab}
                    isExpanded={isExpanded}
                    menuItems={item.subMenuItems}
                    navigationPath={navigationPath}
                  />
                </List>
              </Collapse>
            </Fragment>
          )
        }

        return (
          <SideMenuListItem
            isExpanded={isExpanded}
            key={item.key}
            disableTab={disableTab}
            menuItem={{
              ...item,
              active: item.active === undefined ? isActive(navigationPath, item.link) : item.active,
            }}
          />
        )
      })}
    </>
  )
}
